import { useCallback, useEffect, useState } from "react"
import type { WorkerScenario } from "@/components/ats-fbo/types"

export interface UseConfirmCreateContactProps {
  workerScenario: WorkerScenario | null
  contactOptionsCount: number
  onCreateContact: () => void
}

export interface UseConfirmCreateContactReturn {
  /** True while the inline "are you sure?" prompt is shown above the create-contact button. */
  isConfirming: boolean
  /** True when the email already has at least one contact — the confirmation gate only fires
   *  in this case, since otherwise there's nothing to confuse the user with. */
  hasExistingContactsForEmail: boolean
  /** Wired to the create-contact button: opens the prompt when matches exist, otherwise
   *  commits the create directly. */
  attemptCreate: () => void
  /** Cancels the prompt without creating. */
  cancel: () => void
}

/**
 * Two-step confirmation for the "Add new worker contact" affordance. Without this gate, a
 * single click silently creates a duplicate contact even when the dropdown is showing the
 * existing matches right above the button. The prompt resets whenever the worker scenario
 * changes (e.g. user picked an existing contact, or undid a creation), so it can never get
 * stuck on a stale confirmation.
 */
export function useConfirmCreateContact({
  workerScenario,
  contactOptionsCount,
  onCreateContact,
}: UseConfirmCreateContactProps): UseConfirmCreateContactReturn {
  const [isConfirming, setIsConfirming] = useState(false)
  const hasExistingContactsForEmail = contactOptionsCount > 0

  useEffect(() => {
    setIsConfirming(false)
  }, [workerScenario, contactOptionsCount])

  const attemptCreate = useCallback(() => {
    if (hasExistingContactsForEmail) {
      setIsConfirming(true)
    } else {
      onCreateContact()
    }
  }, [hasExistingContactsForEmail, onCreateContact])

  const cancel = useCallback(() => {
    setIsConfirming(false)
  }, [])

  return { isConfirming, hasExistingContactsForEmail, attemptCreate, cancel }
}
